/** Cab-over tractor unit; its headlights are attached by the room, not this factory. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

function part(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function box(
  size: readonly [number, number, number],
  position: readonly [number, number, number],
  material: THREE.Material,
  name: string,
): THREE.Mesh {
  const mesh = part(new THREE.BoxGeometry(...size), material, name);
  mesh.position.set(...position);
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'truck-cab';

  const body = surface('metal', 0x4a5563);
  const chassis = surface('metal', 0x2e3438);
  const tyre = surface('organic', 0x1e2122);

  const underframe = new THREE.Group();
  underframe.name = 'chassis';
  for (const [x, name] of [
    [-0.45, 'chassis-rail-left'],
    [0.45, 'chassis-rail-right'],
  ] as const) {
    underframe.add(box([0.12, 0.15, 5.6], [x, 0.925, 0], chassis, name));
  }
  underframe.add(box([0.9, 0.06, 0.9], [0, 1.05, -1.2], chassis, 'fifth-wheel-plate'));
  group.add(underframe);

  group.add(box([2.5, 2.25, 2.5], [0, 2.175, 1.55], body, 'cab-body'));
  // Glazing is its OWN near-black value, not the chassis grey it was first
  // built from. Against the 0x4a5563 body, 0x2e3438 lands in the same band of
  // the toon ramp — the first turnaround came back with a cab that had no
  // windows at all and read as a shipping container on wheels. The ramp is what
  // decides whether two albedos are different, not the hex distance between
  // them, and glass at night is the darkest thing on a vehicle.
  const glazing = surface('metal', 0x14181d);
  // PROUD of the body face, not inside it. Both panels were first placed at
  // z 2.73 and x +/-1.18 against a body that reaches z 2.80 and x +/-1.25, so
  // every one of them was authored INSIDE the cab and rendered nothing. The
  // turnaround showed a windowless box and the code read as if it had windows,
  // which is the expensive kind of wrong: the parts were all there.
  group.add(box([2.2, 0.8, 0.03], [0, 2.55, 2.805], glazing, 'front-windscreen'));
  for (const [x, name] of [
    [-1.255, 'side-window-left'],
    [1.255, 'side-window-right'],
  ] as const) {
    group.add(box([0.03, 0.8, 1.25], [x, 2.55, 1.7], glazing, name));
  }

  const deflectorGeometry = new THREE.BufferGeometry();
  deflectorGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(
      new Float32Array([
        -1.25, 3.3, 0.6,
        1.25, 3.3, 0.6,
        1.25, 3.3, 2.8,
        -1.25, 3.3, 2.8,
        -1.25, 3.05, 0.6,
        1.25, 3.05, 0.6,
        1.25, 3.3, 2.8,
        -1.25, 3.3, 2.8,
      ]),
      3,
    ),
  );
  deflectorGeometry.setIndex([
    0, 1, 2,
    0, 2, 3,
    0, 4, 5,
    0, 5, 1,
    1, 5, 6,
    1, 6, 2,
    2, 6, 7,
    2, 7, 3,
    3, 7, 4,
    3, 4, 0,
  ]);
  deflectorGeometry.computeVertexNormals();
  group.add(part(deflectorGeometry, body, 'roof-deflector'));

  const grille = new THREE.Group();
  grille.name = 'front-grille';
  for (let index = 0; index < 5; index += 1) {
    grille.add(
      box(
        [1.45, 0.06, 0.06],
        [0, 1.4 + index * 0.12, 2.77],
        chassis,
        `grille-bar-${index + 1}`,
      ),
    );
  }
  group.add(grille);

  const lights = new THREE.Group();
  lights.name = 'headlight-housings';
  for (const [x, side] of [
    [-0.85, 'left'],
    [0.85, 'right'],
  ] as const) {
    lights.add(box([0.52, 0.36, 0.04], [x, 1.2, 2.78], chassis, `headlight-${side}-rim`));
    lights.add(box([0.42, 0.26, 0.12], [x, 1.2, 2.74], body, `headlight-${side}-housing`));
    // Empty anchors keep factory code free of lights and cameras, as required by
    // the asset contract, while letting the room place its own SpotLights.
    const anchor = new THREE.Group();
    anchor.name = `headlight-${side}`;
    anchor.position.set(x, 1.2, 2.86);
    lights.add(anchor);
  }
  group.add(lights);

  const runningGear = new THREE.Group();
  runningGear.name = 'running-gear';
  for (const [z, axle] of [
    [1.9, 'front'],
    [-1.8, 'rear'],
  ] as const) {
    for (const [x, side] of [
      [-0.99, 'left'],
      [0.99, 'right'],
    ] as const) {
      const wheel = part(
        new THREE.CylinderGeometry(0.52, 0.52, 0.3, 12),
        tyre,
        `${axle}-wheel-${side}`,
      );
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x, 0.52, z);
      runningGear.add(wheel);
    }
  }
  group.add(runningGear);

  for (const [x, side] of [
    [-1.18, 'left'],
    [1.18, 'right'],
  ] as const) {
    const stepX = x < 0 ? -1.17 : 1.17;
    group.add(
      box([0.16, 0.05, 0.45], [stepX, 1.13, 1.45], chassis, `upper-step-${side}`),
      box([0.16, 0.05, 0.45], [stepX, 1.0, 1.0], chassis, `lower-step-${side}`),
    );
    group.add(box([0.05, 0.48, 0.05], [x, 1.5, 1.15], chassis, `grab-rail-${side}`));
  }

  for (const [x, side] of [
    [-1.2, 'left'],
    [1.2, 'right'],
  ] as const) {
    group.add(box([0.06, 0.28, 0.06], [x, 2.72, 2.68], chassis, `mirror-arm-${side}`));
  }

  return group;
}

export const truckCabAsset: AssetDefinition = {
  id: 'truck-cab',
  label: 'Truck cab',
  description: 'Stationary cab-over tractor unit for the loading yard.',
  footprint: { width: 2.5, height: 3.3, length: 5.6 },
  create: build,
};
